<template>
	<div class="pricing-form-container">
		<Form ref="pricingForm" @submit="handleSubmit">
			<!-- Station Field -->
			<div class="col-md-8">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-start align-items-center mb-2">
					<label class="form-label fw-normal col-12 col-md-6">Station</label>
					<div class="col-12 col-md-6">
						<SelectField
							id="station"
							name="station"
							label="Select station"
							v-model="formData.station"
							:options="station_options"
							rules="required"
						/>
						
					
					</div>
				</div>
			</div>

			<!-- Battery Type Field -->
			<div class="col-md-8">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-start align-items-center mb-2">
					<label class="form-label fw-normal col-12 col-md-6">Battery Type</label>
					<div class="col-12 col-md-6">
						<SelectField
							id="type"
							name="type"
							label="Select battery type"
							v-model="formData.type"
							:options="batteryTypeOptions"
							rules="required"
						>
							<option v-for="type in batteryTypes" :key="type.value" :value="type.value">
								{{ type.name }}
							</option>
						</SelectField>
					</div>
				</div>
			</div>

			<!-- Pricing Field -->
			<div class="col-md-8">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-start align-items-center mb-2">
					<label class="form-label fw-normal col-12 col-md-6">Price</label>
					<div class="col-12 col-md-6">
						<MoneyField 
							id="amount"
							name="amount"
							label="amount"
							v-model="formData.amount"
							currency="NGN"
							placeholder="₦0.00"
							locale="en-NG"
							rules="required"
						/>
					</div>
				</div>
			</div>

			<!-- Activated At Field -->
			<div class="col-md-8">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-start align-items-center mb-2">
					<label class="form-label fw-normal col-12 col-md-6">Effective Date</label>
					<div class="col-12 col-md-6">
						<DateField 
							id="date"
							name="date"
							type="date"
							class="form-control" 
							v-model="formData.date"
							label="Activation date"
							rules="required|date:DD-MM-YYYY"
						/>
					</div>
				</div>
			</div>

			<!-- Action Buttons -->
			<div class="col-md-8">
				<div class="d-flex gap-2 justify-content-end mt-3">
					<button 
						type="button" 
						class="btn bg-danger text-light px-4" 
						@click="handleCancel"
						:disabled="loading"
					>
						Cancel
					</button>
					<button 
						type="submit" 
						class="btn bg-secondary text-light px-4" 
						:disabled="loading || (!updateForm && !canSubmit)"
					>
						<span v-if="loading">
							<i class="fas fa-spinner fa-spin me-2"></i>Processing...
						</span>
						<span v-else>
							<i class="fas fa-plus me-2"></i>{{ updateForm ? `Update Record`: `Add Pricing`}}
						</span>
					</button>
				</div>
			</div>
		</Form>
	</div>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative, Emit } from 'vue-facing-decorator'
import Form from '@/vue/components/Form.vue'
import SelectField from '@/vue/components/SelectField.vue'
import InputField from '@/vue/components/InputField.vue'
import MoneyField from '@/vue/components/MoneyField.vue'
import DateField from '@/vue/components/DateField.vue'
import type { Options as Select2Options } from 'select2'
import $ from 'jquery'
import moment from 'moment'

interface PricingFormData {
	station: string
	type: string
	amount: number
	date: string
}

interface Station {
	value: string
	name: string
}

interface type {
	value: string
	name: string
}

@Component({
	components: {
		Form,
		SelectField,
		InputField,
		MoneyField,
        DateField,
	}
})
class PricingForm extends Vue {
	loading = false

	@Prop({ type: String, required: true })
	readonly url!: string

	@Prop({ type: Array, default: () => [] })
	readonly stations!: Station[]

	@Prop({ type: Array, default: () => [] })
	readonly batteryTypes!: BatteryType[]

	@Prop({type:Boolean})
	readonly updateForm!: boolean

	@Prop({type:Object, default: null})
	readonly priceData!: any

	formData: PricingFormData = {
		station: '',
		type: '',
		amount: 0,
		date :  moment().format('DD-MM-YYYY')
	}

    get station_options(): Select2Options {
		return { 
			placeholder: 'Select Station',
			//minimumResultsForSearch: Infinity,
			ajax: {
				method: 'get',
				url: this.route('station.ajax.select.station'),
			},
			data: this.priceData?.station? [{
			id: this.priceData?.station?.slug,
			text: `${this.priceData?.station?.title}`,
			title: `${this.priceData?.station?.title}`,
			}]:[],
			//@ts-ignore
			templateResult: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
		}
	}

	get batteryTypeOptions(): Select2Options {
		return {
			placeholder: 'Select Battery Type',
			minimumResultsForSearch: Infinity,
			ajax: null
		}
	}

	get canSubmit(): boolean {
		return !!(
			this.formData.station &&
			this.formData.type &&
			this.formData.amount > 0 &&
			this.formData.date
		)
	}

	created() {
		if (this.updateForm) {
			this.$nextTick()
			this.prefillForm()
		}
		
	}

	prefillForm() {
		if (this.updateForm && this.priceData) {
			this.formData.station = this.priceData?.station?.slug || ''
			this.formData.type = this.priceData.type
			this.formData.amount = this.priceData.amount.amount / 100
			this.formData.date = moment(this.priceData.activatedAt).format('DD-MM-YYYY')
		}
	}

	async handleSubmit(payload: any) {
		try {
			this.setLoading(true)

			const submissionData = {
				...payload,
				amount: payload.amount * 100, // Convert to kobo
			}

			const response = this.updateForm ? await this.$http.put(this.url,submissionData)
							: await this.$http.post(this.url, submissionData)

			this.onSuccess(response)
			this.resetForm()

		} catch (error) {
			this.onError(error)
		} finally {
			this.setLoading(false)
		}
	}

	@Emit('success')
	onSuccess(response: any) {
		return {
			message: response?.data?.message || 'Pricing added successfully!',
			data: response?.data
		}
	}

	@Emit('error')
	onError(error: any) {
		return {
			message: error.response?.data?.message || 'Failed to add pricing. Please try again.',
			error
		}
	}

	setLoading(state: boolean) {
		return this.loading = state
	}

	@Emit('cancel')
	handleCancel() {
		if (!this.loading) { 
			this.resetForm()
			return true
		}
		return false
	}

	resetForm() {
		this.formData = {
			station: '',
			type: '',
			amount: 0,
			date: new Date().toISOString().split('T')[0]
		}
	}
}

export default toNative(PricingForm)
</script>

<style scoped>
.pricing-form-container {
	width: 100%;
}

.form-label {
	margin-bottom: 0.5rem;
	color: #6c757d;
}

@media (max-width: 768px) {
	.col-md-6 {
		margin-bottom: 0.5rem;
	}
}
</style>